import { lazy } from 'react';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import { AuthGuard } from 'routes/auth-guard';

const Sites = Loadable(lazy(() => import('views/sites/sites.page')));
const CreateSite = Loadable(lazy(() => import('views/sites/pages/CreateSite.page')));
const EditSite = Loadable(lazy(() => import('views/sites/pages/EditSite.page')));
const Categories = Loadable(lazy(() => import('views/categories/categories.page')));
const CreateCategory = Loadable(lazy(() => import('views/categories/pages/CreateCategory.page')));
const EditCategory = Loadable(lazy(() => import('views/categories/pages/EditCategory.page')));
const Contents = Loadable(lazy(() => import('views/contents/contents.page')));
const CreateContent = Loadable(lazy(() => import('views/contents/pages/CreateContent.page')));
const EditContent = Loadable(lazy(() => import('views/contents/pages/EditContent.page')));
const DashboardDefault = Loadable(lazy(() => import('views/dashboard/Default')));
const Tags = Loadable(lazy(() => import('views/tags/tags.page')));
const Pages = Loadable(lazy(() => import('views/pages/pages.page')));
const CreatePages = Loadable(lazy(() => import('views/pages/pages/CreatePage.page')));
const EditPages = Loadable(lazy(() => import('views/pages/pages/EditPage.page')));
const Users = Loadable(lazy(() => import('views/users/Users.page')));
const CreateUser = Loadable(lazy(() => import('views/users/pages/CreateUser.page')));
const EditUser = Loadable(lazy(() => import('views/users/pages/EditUser.page')));
const Disclosures = Loadable(lazy(() => import('views/disclosures/disclosures.page')));
const EditDisclosure = Loadable(lazy(() => import('views/disclosures/pages/EditDisclosure.page')));
const CreateDisclosure = Loadable(lazy(() => import('views/disclosures/pages/CreateDisclosure.page')));

const Partners = Loadable(lazy(() => import('views/partners/partners.page')));
const EditPartner = Loadable(lazy(() => import('views/partners/pages/EditPartner.page')));
const CreatePartner = Loadable(lazy(() => import('views/partners/pages/CreatePartner.page')));
const Widgets = Loadable(lazy(() => import('views/widgets/widgets.page')));
const Company = Loadable(lazy(() => import('views/company/company.page')));
const Companies = Loadable(lazy(() => import('views/companies/companies.page')));
const CreateCompany = Loadable(lazy(() => import('views/companies/pages/createCompany.page')));
const EditCompany = Loadable(lazy(() => import('views/companies/pages/editCompany.page')));
const Profiles = Loadable(lazy(() => import('views/profiles/profiles.page')));
const ContentTypesPage = Loadable(lazy(() => import('views/content-types/content-types.page')));

import ToastProvider from 'store/toastAlertContext';
import DialogProvider from 'store/dialogContext';
import ProtectedRoute from './ProtectedRoute';

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  element: (
    <AuthGuard>
      <ToastProvider>
        <DialogProvider>
          <MainLayout />
        </DialogProvider>
      </ToastProvider>
    </AuthGuard>
  ),
  children: [
    {
      path: '/',
      element: <DashboardDefault />
    },
    {
      element: <ProtectedRoute page="contents" action="view" />,
      children: [
        {
          path: 'contents',
          element: <Contents />
        }
      ]
    },
    {
      path: 'contents',
      children: [
        {
          element: <ProtectedRoute page="contents" action="create" />,
          children: [
            {
              path: 'add',
              element: <CreateContent />
            }
          ]
        },
        {
          element: <ProtectedRoute page="contents" action="edit" />,
          children: [
            {
              path: 'edit/:id',
              element: <EditContent />
            }
          ]
        },
        {
          element: <ProtectedRoute page="tags" action="view" />,
          children: [
            {
              path: 'tags',
              element: <Tags />
            }
          ]
        },
        {
          element: <ProtectedRoute page="categories" action="view" />,
          children: [
            {
              path: 'categories',
              element: <Categories />
            }
          ]
        },
        {
          element: <ProtectedRoute page="contenttypes" action="view" />,
          children: [
            {
              path: 'contenttypes',
              element: <ContentTypesPage />
            }
          ]
        },
        {
          path: 'categories',
          children: [
            {
              element: <ProtectedRoute page="categories" action="create" />,
              children: [
                {
                  path: 'add',
                  element: <CreateCategory />
                }
              ]
            },
            {
              element: <ProtectedRoute page="categories" action="create" />,
              children: [
                {
                  path: 'add/:parentId',
                  element: <CreateCategory />
                }
              ]
            },
            {
              element: <ProtectedRoute page="categories" action="edit" />,
              children: [
                {
                  path: 'edit/:id',
                  element: <EditCategory />
                }
              ]
            }
          ]
        }
      ]
    },
    {
      element: <ProtectedRoute page="sites" action="view" />,
      children: [
        {
          path: 'sites',
          element: <Sites />
        }
      ]
    },
    {
      path: 'sites',
      children: [
        {
          element: <ProtectedRoute page="sites" action="create" />,
          children: [
            {
              path: 'add',
              element: <CreateSite />
            }
          ]
        },
        {
          element: <ProtectedRoute page="categories" action="edit" />,
          children: [
            {
              path: 'edit/:id',
              element: <EditSite />
            }
          ]
        }
      ]
    },
    {
      element: <ProtectedRoute page="pages" action="view" />,
      children: [
        {
          path: 'pages',
          element: <Pages />
        }
      ]
    },
    {
      path: 'pages',
      children: [
        {
          element: <ProtectedRoute page="pages" action="create" />,
          children: [
            {
              path: 'add',
              element: <CreatePages />
            }
          ]
        },
        {
          element: <ProtectedRoute page="pages" action="edit" />,
          children: [
            {
              path: 'edit/:id',
              element: <EditPages />
            }
          ]
        }
      ]
    },
    {
      element: <ProtectedRoute page="users" action="view" />,
      children: [
        {
          path: 'users',
          element: <Users />
        }
      ]
    },
    {
      path: 'users',
      children: [
        {
          element: <ProtectedRoute page="users" action="create" />,
          children: [
            {
              path: 'add',
              element: <CreateUser />
            }
          ]
        },
        {
          element: <ProtectedRoute page="users" action="edit" />,
          children: [
            {
              path: 'edit/:id',
              element: <EditUser />
            }
          ]
        }
      ]
    },
    {
      element: <ProtectedRoute page="disclosures" action="view" />,
      children: [
        {
          path: 'disclosures',
          element: <Disclosures />
        }
      ]
    },
    {
      path: 'disclosures',
      children: [
        {
          element: <ProtectedRoute page="disclosures" action="create" />,
          children: [
            {
              path: 'add',
              element: <CreateDisclosure />
            }
          ]
        },
        {
          element: <ProtectedRoute page="disclosures" action="edit" />,
          children: [
            {
              path: 'edit/:id',
              element: <EditDisclosure />
            }
          ]
        }
      ]
    },
    {
      element: <ProtectedRoute page="partners" action="view" />,
      children: [
        {
          path: 'partners',
          element: <Partners />
        }
      ]
    },
    {
      path: 'partners',
      children: [
        {
          element: <ProtectedRoute page="partners" action="create" />,
          children: [
            {
              path: 'add',
              element: <CreatePartner />
            }
          ]
        },
        {
          element: <ProtectedRoute page="partners" action="edit" />,
          children: [
            {
              path: 'edit/:id',
              element: <EditPartner />
            }
          ]
        }
      ]
    },
    {
      element: <ProtectedRoute page="companies" action="view" />,
      children: [
        {
          path: 'company',
          element: <Company />
        }
      ]
    },
    {
      element: <ProtectedRoute page="companies" action="view" />,
      children: [
        {
          path: 'organizations',
          element: <Companies />
        }
      ]
    },
    {
      path: 'organizations',
      children: [
        {
          element: <ProtectedRoute page="companies" action="create" />,
          children: [
            {
              path: 'add',
              element: <CreateCompany />
            }
          ]
        },
        {
          element: <ProtectedRoute page="companies" action="edit" />,
          children: [
            {
              path: 'edit/:id',
              element: <EditCompany />
            }
          ]
        }
      ]
    },
    {
      element: <ProtectedRoute page="profiles" action="view" />,
      children: [
        {
          path: 'profiles',
          element: <Profiles />
        }
      ]
    },
    {
      path: 'widgets',
      element: <Widgets />
    }
  ]
};

export default MainRoutes;
